import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, BrainCircuit, CheckCircle2, Clock, Database, Loader2, RefreshCw } from 'lucide-react';
import {
  trainingApi,
  type TrainingProgramme,
  type TrainingStoreStatus,
} from '../services/api';
import PipelinePanel from '../components/training/PipelinePanel';
import ProgressPanel from '../components/training/ProgressPanel';
import ProgrammeMap from '../components/training/ProgrammeMap';
import TrainingTabs from '../components/training/TrainingTabs';

/**
 * Model Training — the programme picker and a programme's overview (EP-26 FE-2).
 *
 * Every page of the module is scoped to ONE training programme, so the choice is
 * in the URL (`/training/:programId`) — links carry it — and is remembered per
 * browser so returning without one restores the last programme. The list comes
 * from the server, which returns only programmes the caller holds a grant for;
 * nothing here decides access.
 */
const REMEMBER_KEY = 'pmo.training.programId';

function remembered(): string {
  try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
}
function remember(id: string) {
  try { localStorage.setItem(REMEMBER_KEY, id); } catch { /* private mode */ }
}

const STATE_STYLE: Record<TrainingStoreStatus['state'], { label: string; cls: string }> = {
  ok: { label: 'Ready', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  not_provisioned: { label: 'Not provisioned', cls: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  unreachable: { label: 'Unreachable', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  forbidden: { label: 'No access', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  no_credentials: { label: 'No credential', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
};

export default function TrainingPage() {
  const { programId } = useParams<{ programId?: string }>();
  const navigate = useNavigate();
  const [programmes, setProgrammes] = useState<TrainingProgramme[] | null>(null);
  const [stores, setStores] = useState<TrainingStoreStatus[] | null>(null);
  const [loadingStores, setLoadingStores] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trainingApi.programmes()
      .then(setProgrammes)
      .catch((e) => { setError(String(e?.message || e)); setProgrammes([]); });
  }, []);

  // No programme in the URL: go to the remembered one, else the first visible.
  useEffect(() => {
    if (!programmes || programId) return;
    const last = remembered();
    const target = programmes.find((p) => p._id === last) || programmes[0];
    if (target) navigate(`/training/${encodeURIComponent(target._id)}`, { replace: true });
  }, [programmes, programId, navigate]);

  const current = programmes?.find((p) => p._id === programId) || null;

  const loadStores = useCallback(() => {
    if (!programId) return;
    setLoadingStores(true);
    trainingApi.stores(programId)
      .then((r) => setStores(r.stores))
      .catch((e) => { setError(String(e?.message || e)); setStores([]); })
      .finally(() => setLoadingStores(false));
  }, [programId]);

  useEffect(() => {
    if (!programId) return;
    remember(programId);
    setStores(null);
    loadStores();
  }, [programId, loadStores]);

  if (programmes === null) {
    return <div className="p-8 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading programmes…</div>;
  }

  if (programmes.length === 0) {
    return (
      <div className="p-8 max-w-xl">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <BrainCircuit className="w-5 h-5" /> Model Training
        </h1>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          You don't have access to any training programme yet. Establish a new model for training, or ask an
          organisation admin to add you to an existing programme's groups.
        </p>
        <button type="button" onClick={() => navigate('/training/new')}
          className="mt-3 px-3 py-1.5 text-sm rounded bg-accent text-white">New model for training</button>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  const profile = current?.profile || {};
  const marks = profile.passMarks;

  return (
    <div className="p-6 space-y-6">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <BrainCircuit className="w-5 h-5" /> Model Training
        </h1>
        <label htmlFor="training-programme" className="sr-only">Programme</label>
        <select
          id="training-programme"
          value={programId || ''}
          onChange={(e) => navigate(`/training/${encodeURIComponent(e.target.value)}`)}
          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg"
        >
          {programmes.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <button type="button" onClick={() => navigate('/training/new')}
          className="ml-auto px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 hover:border-accent">
          New model for training</button>
      </header>

      {current ? <TrainingTabs programId={current._id} /> : null}

      {!current ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">That programme isn't available to you. Pick one above.</p>
      ) : (
        <>
          <ProgrammeMap programId={current._id} />
          <ProgressPanel programId={current._id} />

          <section className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Trains on</div>
              <div className="mt-1 text-lg font-medium text-gray-900 dark:text-gray-100">
                {profile.unit?.label || profile.unit?.name || '—'}
              </div>
              {profile.aggregation?.length ? (
                <div className="mt-1 text-xs text-gray-500">Rolls up: {profile.aggregation.join(' → ')}</div>
              ) : null}
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Pass marks</div>
              {marks ? (
                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100 space-y-0.5 tabular-nums">
                  <div>{marks.mainScoreName || 'score'} ≥ {marks.mainScoreMin}</div>
                  <div>unsafe ≤ {Math.round(marks.unsafeMax * 1000) / 10}%</div>
                  {marks.minEvidence ? <div>≥ {marks.minEvidence} evidence per unit</div> : null}
                </div>
              ) : <div className="mt-1 text-sm text-gray-500">—</div>}
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">Regression tolerance</div>
              <div className="mt-1 text-sm text-gray-900 dark:text-gray-100 tabular-nums">
                {profile.regressionTolerance
                  ? <>−{profile.regressionTolerance.mainScoreDrop} score, +{profile.regressionTolerance.unsafeRise} unsafe</>
                  : '—'}
              </div>
              {profile.depth ? (
                <div className="mt-1 text-xs text-gray-500">Depth {profile.depth.rowsPerUnit} rows/unit × {profile.depth.repetition}</div>
              ) : null}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Database className="w-4 h-4" /> Stores
              </h2>
              <button
                type="button"
                onClick={loadStores}
                className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingStores ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
            <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500">
                  <tr><th className="text-left p-2">Role</th><th className="text-left p-2">Database</th>
                    <th className="text-left p-2">State</th><th className="text-right p-2">Documents</th></tr>
                </thead>
                <tbody>
                  {(stores || []).map((s) => (
                    <tr key={s.role} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="p-2 text-gray-900 dark:text-gray-100">{s.role}</td>
                      <td className="p-2 font-mono text-xs text-gray-600 dark:text-gray-300">{s.db}</td>
                      <td className="p-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${STATE_STYLE[s.state].cls}`}>
                          {s.state === 'ok' ? <CheckCircle2 className="w-3 h-3" />
                            : s.state === 'not_provisioned' ? <Clock className="w-3 h-3" />
                            : <AlertTriangle className="w-3 h-3" />}
                          {STATE_STYLE[s.state].label}
                        </span>
                        {s.pendingTask ? <span className="ml-2 text-xs text-gray-500">waiting on {s.pendingTask}</span> : null}
                      </td>
                      <td className="p-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                        {s.state === 'ok' ? (s.docCount ?? 0).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                  {stores === null && (
                    <tr><td colSpan={4} className="p-3 text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin" /> Checking stores…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <PipelinePanel programId={current._id} />
        </>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
